import { Button, Form, InputField, THEME } from "@myloc/myloc-gui";
import PropType from "prop-types";
import { useState } from "react";
import { useTranslate } from "../../../../language/i18n";
import { setClient } from "../../../../reducers/appData/appDataActions";
import clientService from "../../../../services/client/clientService";
import { DECEASED_DEREGISTRATION_CODE } from "../../../../utils/constants";
import { COMMON_REGEX } from "../../../../utils/regExUtils";
import CustomerSelector from "./Customer/CustomerSelector";
import styles from "./UserSelector.module.scss";

const UserSelector = ({ onComplete }) => {
  const translate = useTranslate();

  const [identityNumber, setIdentityNumber] = useState("");
  const [identityError, setIdentityError] = useState();
  const [identityWarning, setIdentityWarning] = useState();
  const [isValidIdentity, setIsValidIdentity] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentUser, setCurrentUser] = useState();
  const [userIsFromNavet, setUserIsFromNavet] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const validateIdentityNumber = e => {
    const id = e.target.value;
    if (id && id.length < 1) setIdentityError(translate("PERSONAL_IDENTITY_NUMBER_EMPTY"));
  };
  const validateIfRegularIdentityNumber = identityNumber => {
    if (identityNumber && COMMON_REGEX.IDENTITY_NUMBER_SWE.test(identityNumber)) return true;
    return false;
  };

  const onChangeIdentityNumber = e => {
    setIdentityNumber(e.target.value);
    setHasSearched(false);
    setCurrentUser(null);

    if (e.target.value && e.target.value.length > 0) {
      setIdentityError(undefined);
      setIsValidIdentity(true);
    } else {
      setIsValidIdentity(false);
    }
  };

  async function onSearch(e, searchNavet) {
    e.preventDefault();
    setLoading(true);
    const response = await clientService.findNavetClient(identityNumber, searchNavet);

    if (response?.data?.page?.length) {
      setIdentityError(null);
      setIdentityWarning(null);
      setCurrentUser(response.data.page?.[0]);
      setUserIsFromNavet(searchNavet);
    } else {
      if (response?.data?.count == 0) {
        setIdentityWarning(translate("CLIENT_NOT_FOUND"));
        if (validateIfRegularIdentityNumber(identityNumber)) setHasSearched(true);
      } else setIdentityWarning(translate("NOT_AUTHORIZED_TO_MUNICIPALITY"));
    }
    setLoading(false);
  }

  function onNext(id) {
    onComplete(id);
  }

  return (
    <>
      <Form id="search" onSubmit={onSearch}>
        <InputField
          autoFocus
          label={translate("PERSONAL_IDENTITY_NUMBER_OR_UNIT")}
          placeholder="YYYYMMDD-XXXX"
          type={"text"}
          required
          value={identityNumber}
          onBlur={validateIdentityNumber}
          customInvalidMessage={identityError}
          customWarningMessage={identityWarning}
          onChange={onChangeIdentityNumber}
        />
        {!currentUser && (
          <div className={styles.buttonBase}>
            <Button disabled={!isValidIdentity} isLoading={isLoading} type="submit">
              {translate("SEARCH")}
            </Button>
            {hasSearched && (
              <Button
                theme={THEME.SECONDARY}
                disabled={!isValidIdentity}
                isLoading={isLoading}
                onClick={e => onSearch(e, true)}
              >
                {translate("SEARCH_IN_NAVET")}
              </Button>
            )}
          </div>
        )}
      </Form>
      {!!currentUser && (
        <UserData
          user={currentUser}
          setUserData={setCurrentUser}
          onNext={onNext}
          userIsFromNavet={userIsFromNavet}
          setUserIsFromNavet={setUserIsFromNavet}
          identityNumber={identityNumber}
        />
      )}
    </>
  );
};

const UserData = ({ user, setUserData, onNext, userIsFromNavet, setUserIsFromNavet, identityNumber }) => {
  const translate = useTranslate();
  const [isLoading, setLoading] = useState(false);
  const [chosenMunicipality, setChosenMunicipality] = useState(null);

  const setContactInformation = value => {
    const contactInformation = Object.assign({}, user.contactInformation, value);
    setUserData(Object.assign({}, user, { contactInformation: contactInformation }));
  };

  async function onSubmit(e) {
    setLoading(true);
    e.preventDefault();

    async function updateClient(user) {
      return await clientService.updateClient(user.id, {
        contactInformation: user.contactInformation,
        municipalAffiliation: chosenMunicipality,
        useAdvanceContact: user.useAdvanceContact,
      });
    }

    async function createClient() {
      const responseCreate = await clientService.createClient(identityNumber);

      if (responseCreate.isOk()) {
        setClient(responseCreate.data);
        return await updateClient(responseCreate.data);
      }
    }

    const response = userIsFromNavet ? await createClient() : await updateClient(user);

    if (response?.isOk()) {
      setClient(response.data);
      onNext(response.data.id);
      setUserIsFromNavet(false);
    }

    setLoading(false);
  }

  const onSelect = selected => {
    setChosenMunicipality(selected);
  };

  const disabled = !user.municipalAffiliation && !chosenMunicipality;
  const isDeceased = user.deregistrationReason?.value === DECEASED_DEREGISTRATION_CODE;
  return (
    <>
      <div className={styles.information}>
        <p className={`${styles.value} ${isDeceased ? styles.deceasedName : ""}`}>
          {user.firstName} {user.surname}
        </p>
        <p className={styles.value}>{user.defaultAddress?.street}</p>
        <p className={styles.value}>
          {user.defaultAddress?.postalNo} {user.defaultAddress?.city}
        </p>
      </div>
      {isDeceased && <p className={styles.deceasedInfo}>{translate("CLIENT_DECEASED_ORDER_RESTRICTION")}</p>}
      <Form onSubmit={onSubmit}>
        {!user.municipalAffiliation ? (
          <CustomerSelector
            municipalAffiliation={user.municipalAffiliation}
            onSelect={onSelect}
            chosenMunicipality={chosenMunicipality}
          />
        ) : (
          <InputField readOnly label={translate("MUNICIPALITY_AFFILIATION")} value={user.municipalAffiliation.name} />
        )}

        <InputField
          autoFocus
          label={translate("EMAIL")}
          type={"email"}
          value={user.contactInformation.email}
          onChange={e => setContactInformation({ email: e.target.value })}
        />
        <InputField
          label={translate("PHONE_NO")}
          type={"tel"}
          value={user.contactInformation.phoneNo}
          onChange={e => setContactInformation({ phoneNo: e.target.value })}
        />
        <div className={styles.buttonBase}>
          <Button type="submit" isLoading={isLoading} disabled={disabled}>
            {translate("NEXT")}
          </Button>
        </div>
      </Form>
    </>
  );
};

export default UserSelector;

UserSelector.propTypes = {
  onComplete: PropType.func.isRequired,
};

UserData.propTypes = {
  user: PropType.object,
  setUserData: PropType.func,
  onNext: PropType.func,
  userIsFromNavet: PropType.bool,
  setUserIsFromNavet: PropType.func,
  identityNumber: PropType.string,
};
